<template>
  <div class="relative">
    <button
      :id="buttonId"
      @click="toggleDropdown"
      :class="resolvedButtonClass"
      type="button"
    >
      Precio
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="13"
        height="7"
        viewBox="0 0 13 7"
        fill="none"
        :class="['transition-transform duration-200 shrink-0', showDropdown ? 'rotate-180' : '']"
      >
        <path fill-rule="evenodd" clip-rule="evenodd" d="M0.146894 0.146894C0.19334 0.10033 0.248515 0.0633873 0.30926 0.0381808C0.370005 0.0129744 0.435127 0 0.500894 0C0.566661 0 0.631782 0.0129744 0.692528 0.0381808C0.753273 0.0633873 0.808448 0.10033 0.854894 0.146894L6.50089 5.79389L12.1469 0.146894C12.1934 0.100406 12.2486 0.0635292 12.3093 0.0383701C12.37 0.013211 12.4352 0.000261784 12.5009 0.000261784C12.5666 0.000261784 12.6317 0.013211 12.6925 0.0383701C12.7532 0.0635292 12.8084 0.100406 12.8549 0.146894C12.9014 0.193381 12.9383 0.248571 12.9634 0.30931C12.9886 0.370049 13.0015 0.43515 13.0015 0.500894C13.0015 0.566637 12.9886 0.631738 12.9634 0.692477C12.9383 0.753217 12.9014 0.808406 12.8549 0.854894L6.85489 6.85489C6.80845 6.90146 6.75327 6.9384 6.69253 6.96361C6.63178 6.98881 6.56666 7.00179 6.50089 7.00179C6.43513 7.00179 6.37001 6.98881 6.30926 6.96361C6.24852 6.9384 6.19334 6.90146 6.14689 6.85489L0.146894 0.854894C0.100331 0.808448 0.0633878 0.753272 0.0381813 0.692527C0.0129749 0.631782 0 0.566661 0 0.500894C0 0.435126 0.0129749 0.370005 0.0381813 0.30926C0.0633878 0.248515 0.100331 0.193339 0.146894 0.146894Z" fill="#FF4265"/>
      </svg>
    </button>

    <div
      :id="menuId"
      :class="dropdownClasses"
      v-show="showDropdown"
    >
      <ul class="z-[999] p-2 text-sm text-body font-medium" :aria-labelledby="buttonId">
        <li class="flex items-center justify-between">
          <a class="shrink-0 py-2 hover:bg-neutral-tertiary-medium hover:text-heading rounded">Tipo de <br> cambio</a>
          <div class="ml-auto">
            <PriceDropDown
              :modelValue="localCurrency"
              @update:modelValue="onCurrencyChange"
              @change="onChange"
            />
          </div>
        </li>
        <li>
          <a class="inline-flex items-center w-full py-2 hover:bg-neutral-tertiary-medium hover:text-heading rounded">Mínimo</a>
          <input
            type="text"
            placeholder="1,000"
            inputmode="numeric"
            pattern="[0-9,]*"
            class="block h-9 py-4 px-2 w-full z-20 text-sm font-normal text-custom_gray bg-white rounded-lg border border-gray-300 focus:ring-tuin_base-0 focus:border-tuin_base-0 dark:bg-gray-700 dark:border-e-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:border-tuin_base-0"
            :value="localMinPrice"
            @input="handlePriceInput('min', $event)"
            @blur="applyPriceFilter('min')"
            @change="onChange"
          />
        </li>
        <li>
          <a class="inline-flex items-center w-full py-2 hover:bg-neutral-tertiary-medium hover:text-heading rounded">Máximo</a>
          <input
            type="text"
            placeholder="1,000"
            inputmode="numeric"
            pattern="[0-9,]*"
            class="block h-9 py-4 px-2 w-full z-20 text-sm font-normal text-custom_gray bg-white rounded-lg border border-gray-300 focus:ring-tuin_base-0 focus:border-tuin_base-0 dark:bg-gray-700 dark:border-e-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:border-tuin_base-0"
            :value="localMaxPrice"
            @input="handlePriceInput('max', $event)"
            @blur="applyPriceFilter('max')"
            @change="onChange"
          />
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import PriceDropDown from '@/components/common/PriceDropDown.vue'

const props = defineProps({
  currency: { type: String, default: '' },
  minPrice: { type: String, default: '' },
  maxPrice: { type: String, default: '' },
  dropdownAlign: {
    type: String,
    default: 'left',
    validator: (v) => ['left', 'right'].includes(v),
  },
  buttonClass: { type: String, default: null },
})

const emit = defineEmits(['update:currency', 'update:minPrice', 'update:maxPrice', 'change'])

const uid = `prf-${(Math.random() * 1e9).toFixed(0)}`
const buttonId = computed(() => `dropdownPrice-${uid}`)
const menuId = computed(() => `dropdownPriceMenu-${uid}`)

const showDropdown = ref(false)

const defaultButtonClass =
  'cursor-pointer gap-2 justify-between inline-flex items-center bg-white border-[#DEE2E6] text-gray-500 rounded-md focus:ring-tuin-100 focus:border-tuin-100 bg-brand box-border border focus:ring-1 shadow-xs leading-5 rounded-base text-sm py-2.5 pr-3 pl-3 w-full md:w-auto focus:outline-none'

const resolvedButtonClass = computed(() => props.buttonClass || defaultButtonClass)

const dropdownClasses = computed(() => {
  const base = 'absolute z-10 mt-1 bg-white rounded-lg shadow-lg md:w-[210px] w-full'
  return props.dropdownAlign === 'right' ? `${base} right-0` : base
})

const localCurrency = ref(props.currency)
const localMinPrice = ref(props.minPrice)
const localMaxPrice = ref(props.maxPrice)

watch(() => props.currency, (v) => { if (v !== localCurrency.value) localCurrency.value = v })
watch(() => props.minPrice, (v) => { if (v !== localMinPrice.value) localMinPrice.value = v })
watch(() => props.maxPrice, (v) => { if (v !== localMaxPrice.value) localMaxPrice.value = v })

function normalizeNumberValue(value) {
  if (!value) return ''
  return String(value).replace(/[^0-9]/g, '')
}

function formatNumberValue(value) {
  const normalized = normalizeNumberValue(value)
  if (!normalized) return ''
  return new Intl.NumberFormat('en-US').format(Number(normalized))
}

function handlePriceInput(field, event) {
  const formattedValue = formatNumberValue(event.target.value)
  if (field === 'min') {
    localMinPrice.value = formattedValue
    emit('update:minPrice', formattedValue)
  } else {
    localMaxPrice.value = formattedValue
    emit('update:maxPrice', formattedValue)
  }
}

function applyPriceFilter(field) {
  if (field === 'min') {
    localMinPrice.value = formatNumberValue(localMinPrice.value)
    emit('update:minPrice', localMinPrice.value)
  } else {
    localMaxPrice.value = formatNumberValue(localMaxPrice.value)
    emit('update:maxPrice', localMaxPrice.value)
  }
  onChange()
}

function onCurrencyChange(value) {
  localCurrency.value = value
  emit('update:currency', value)
}

function toggleDropdown() {
  showDropdown.value = !showDropdown.value
}

function close() {
  showDropdown.value = false
}

function onChange() {
  emit('change', {
    filterCurrency: localCurrency.value,
    filterMinPrice: localMinPrice.value,
    filterMaxPrice: localMaxPrice.value,
  })
}

function handleClickOutside(event) {
  const el = document.getElementById(buttonId.value)
  if (el) {
    const parent = el.closest('.relative')
    if (parent && !parent.contains(event.target)) {
      showDropdown.value = false
    }
  }
}

onMounted(() => document.addEventListener('click', handleClickOutside))
onUnmounted(() => document.removeEventListener('click', handleClickOutside))

defineExpose({ close })
</script>
